import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  CreditCard, Users, Building2, FileText, Mail, QrCode, 
  TrendingUp, Clock, CheckCircle, XCircle, AlertCircle,
  ArrowUpRight, Calendar, Wallet, UserPlus, RefreshCw
} from "lucide-react";
import axios from "axios";

const API = '/api';

// Stat Card Component
const StatCard = ({ title, value, subValue, icon: Icon, color, onClick, trend }) => (
  <div 
    onClick={onClick}
    className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer group"
    data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
        {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color ? color.replace('text-', 'bg-').replace('-600', '-100') : 'bg-gray-100'} group-hover:scale-110 transition-transform`}>
        <Icon className={`w-5 h-5 ${color || 'text-gray-600'}`} />
      </div>
    </div>
    {trend && (
      <div className="flex items-center mt-3 text-xs">
        <ArrowUpRight className="w-3 h-3 text-green-500 mr-1" />
        <span className="text-green-600">{trend}</span>
      </div>
    )}
    <div className="mt-3 text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
      Click to view details <ArrowUpRight className="w-3 h-3 ml-1" />
    </div>
  </div>
);

// Status Card Component  
const StatusCard = ({ title, pending, completed, failed, onPending, onCompleted, onFailed }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
    <div className="space-y-3">
      <div 
        onClick={onPending}
        className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors"
      >
        <div className="flex items-center">
          <Clock className="w-4 h-4 text-yellow-600 mr-2" />
          <span className="text-sm text-yellow-800">Pending</span>
        </div>
        <span className="font-bold text-yellow-700">{pending}</span>
      </div>
      <div 
        onClick={onCompleted}
        className="flex items-center justify-between p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
      >
        <div className="flex items-center">
          <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
          <span className="text-sm text-green-800">Completed</span>
        </div>
        <span className="font-bold text-green-700">{completed}</span>
      </div>
      {failed > 0 && (
        <div 
          onClick={onFailed}
          className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
        >
          <div className="flex items-center">
            <XCircle className="w-4 h-4 text-red-600 mr-2" />
            <span className="text-sm text-red-800">Failed</span>
          </div>
          <span className="font-bold text-red-700">{failed}</span>
        </div>
      )}
    </div>
  </div>
);

/**
 * Kyle Rise Payments Dashboard
 * 
 * Every widget, statistic, and card is interactive.
 * Clicking any figure drills down to the underlying records.
 */
export default function KyleRiseDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("month");

  useEffect(() => {
    fetchSummary();
  }, [dateRange]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let dateFrom = null;
      
      if (dateRange === "today") {
        dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      } else if (dateRange === "week") {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        dateFrom = weekAgo.toISOString();
      } else if (dateRange === "month") {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        dateFrom = monthAgo.toISOString();
      } else if (dateRange === "year") {
        const yearAgo = new Date(now);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        dateFrom = yearAgo.toISOString();
      }

      const params = dateFrom ? `?date_from=${dateFrom}` : "";
      const res = await axios.get(`${API}/kyle-rise/summary${params}`);
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kyle-rise-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments & Passes</h1>
            <p className="text-sm text-gray-500">Kyle Rise Payment Dashboard</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
            <button 
              onClick={fetchSummary}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Revenue Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Revenue"
            value={summary?.revenue?.total_display || "£0.00"}
            icon={Wallet}
            color="text-green-600"
            onClick={() => navigate("/admin/kyle-rise/payments?status=completed")}
          />
          <StatCard
            title="Hourly Payments"
            value={summary?.revenue?.breakdown?.payments?.display || "£0.00"}
            subValue={`${summary?.payments?.completed || 0} completed`}
            icon={CreditCard}
            color="text-blue-600"
            onClick={() => navigate("/admin/kyle-rise/payments?status=completed")}
          />
          <StatCard
            title="Resident Pass Revenue"
            value={summary?.revenue?.breakdown?.resident_passes?.display || "£0.00"}
            subValue={`${summary?.resident_passes?.active || 0} active passes`}
            icon={Users}
            color="text-purple-600"
            onClick={() => navigate("/admin/kyle-rise/resident-passes?status=active")}
          />
          <StatCard
            title="Staff Pass Revenue"
            value={summary?.revenue?.breakdown?.staff_passes?.display || "£0.00"}
            subValue={`${summary?.staff_passes?.active || 0} active passes`}
            icon={Building2}
            color="text-orange-600"
            onClick={() => navigate("/admin/kyle-rise/staff-passes?status=active")}
          />
        </div>

        {/* Counts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Payments"
            value={summary?.payments?.total || 0}
            icon={CreditCard}
            onClick={() => navigate("/admin/kyle-rise/payments")}
          />
          <StatCard
            title="Resident Passes"
            value={summary?.resident_passes?.total || 0}
            subValue={`${summary?.resident_passes?.pending || 0} pending`}
            icon={Users}
            color="text-purple-600"
            onClick={() => navigate("/admin/kyle-rise/resident-passes")}
          />
          <StatCard
            title="Staff Passes"
            value={summary?.staff_passes?.total || 0}
            subValue={`${summary?.staff_passes?.pending || 0} pending`}
            icon={Building2}
            color="text-orange-600"
            onClick={() => navigate("/admin/kyle-rise/staff-passes")}
          />
          <StatCard
            title="Retailers"
            value={summary?.retailers?.total || 0}
            subValue={`${summary?.retailers?.active || 0} active`}
            icon={Building2}
            color="text-teal-600"
            onClick={() => navigate("/admin/kyle-rise/retailers")}
          />
        </div>

        {/* Status Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatusCard
            title="Payment Status"
            pending={summary?.payments?.pending || 0}
            completed={summary?.payments?.completed || 0}
            failed={0}
            onPending={() => navigate("/admin/kyle-rise/payments?status=pending")}
            onCompleted={() => navigate("/admin/kyle-rise/payments?status=completed")}
            onFailed={() => navigate("/admin/kyle-rise/payments?status=failed")}
          />
          <StatusCard
            title="Resident Passes"
            pending={summary?.resident_passes?.pending || 0}
            completed={summary?.resident_passes?.active || 0}
            failed={0}
            onPending={() => navigate("/admin/kyle-rise/resident-passes?status=pending")}
            onCompleted={() => navigate("/admin/kyle-rise/resident-passes?status=active")}
            onFailed={() => navigate("/admin/kyle-rise/resident-passes?status=expired")}
          />
          <StatusCard
            title="Staff Passes"
            pending={summary?.staff_passes?.pending || 0}
            completed={summary?.staff_passes?.active || 0}
            failed={0}
            onPending={() => navigate("/admin/kyle-rise/staff-passes?status=pending")}
            onCompleted={() => navigate("/admin/kyle-rise/staff-passes?status=active")}
            onFailed={() => navigate("/admin/kyle-rise/staff-passes?status=expired")}
          />
        </div>

        {/* Enquiries and Emails Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="New Enquiries"
            value={summary?.interests?.new || 0}
            subValue={`${summary?.interests?.total || 0} total`}
            icon={UserPlus}
            color="text-indigo-600"
            onClick={() => navigate("/admin/kyle-rise/interests?status=new")}
          />
          <StatCard
            title="Contacted"
            value={summary?.interests?.contacted || 0}
            icon={AlertCircle}
            color="text-yellow-600"
            onClick={() => navigate("/admin/kyle-rise/interests?status=contacted")}
          />
          <StatCard
            title="Converted"
            value={summary?.interests?.converted || 0}
            icon={CheckCircle}
            color="text-green-600"
            onClick={() => navigate("/admin/kyle-rise/interests?status=converted")}
          />
          <StatCard
            title="Email Receipts"
            value={summary?.email_logs?.total || 0}
            subValue={`${summary?.email_logs?.sent || 0} sent`}
            icon={Mail}
            color="text-blue-600"
            onClick={() => navigate("/admin/kyle-rise/email-logs")}
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <button
              onClick={() => navigate("/admin/kyle-rise/retailers")}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <Building2 className="w-6 h-6 text-teal-600 mb-2" />
              <span className="text-xs text-gray-600">Retailers</span>
            </button>
            <button
              onClick={() => navigate("/admin/kyle-rise/qr-manager")}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <QrCode className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-xs text-gray-600">QR Codes</span>
            </button>
            <button
              onClick={() => navigate("/admin/kyle-rise/resident-passes")}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <Users className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-xs text-gray-600">Residents</span>
            </button>
            <button
              onClick={() => navigate("/admin/kyle-rise/staff-passes")}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <Building2 className="w-6 h-6 text-orange-600 mb-2" />
              <span className="text-xs text-gray-600">Staff Passes</span>
            </button>
            <button
              onClick={() => navigate("/admin/kyle-rise/interests")}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <UserPlus className="w-6 h-6 text-indigo-600 mb-2" />
              <span className="text-xs text-gray-600">Enquiries</span>
            </button>
            <button
              onClick={() => navigate("/admin/kyle-rise/reports")}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <TrendingUp className="w-6 h-6 text-green-600 mb-2" />
              <span className="text-xs text-gray-600">Reports</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
